import axios from "axios";
import { getTokenAccessor } from "@/lib/auth";
import { createResponseErrorInterceptor } from "@/lib/apiErrorInterceptor";

// Sarah-service base URL. Set via VITE_SARAH_SERVICE_BASE_URL:
//   - Empty/unset  → relative paths (Vite proxy in dev, or Amplify env in prod)
//   - Full URL     → direct calls (e.g. http://localhost:8080)
const SARAH_BASE: string = import.meta.env.VITE_SARAH_SERVICE_BASE_URL || "";

// gpcApi base URL includes /sarah/v1 — path functions are relative to this
const BASE_URL = `${SARAH_BASE}/sarah/v1`;

// Pricing requests: /properties/{propertyId}/pricing/...
export const pricingBasePath = (propertyId: string) =>
  `/properties/${propertyId}/pricing`;

// Platforms scoped to a property: /properties/{propertyId}/platforms
export const platformsPath = (propertyId: string) =>
  `/properties/${propertyId}/platforms`;

// Per-property reference reads shared with GPRT: /properties/{propertyId}/room-types
export const propertyBasePath = (propertyId: string) =>
  `/properties/${propertyId}`;

// Property config: /gpc/property/{propertyId}/config
export const propertyConfigPath = (propertyId: string) =>
  `/gpc/property/${propertyId}/config`;

// Per-property/platform auto-approval: /properties/{propertyId}/platform-configs[/{platformId}]
export const autoApprovalConfigsPath = (propertyId: string) =>
  `/properties/${propertyId}/platform-configs`;

export const autoApprovalConfigPath = (
  propertyId: string,
  platformId: number
) => `${autoApprovalConfigsPath(propertyId)}/${platformId}`;

// GPRT-adjacent pricing endpoints (contract endpoints 6-8) — authorized via GPC
// roles, but live under the /gprt namespace: /gprt/properties/{propertyId}/pricing/requests/{id}
export const gprtPricingRequestPath = (propertyId: string, requestId: string) =>
  `/gprt/properties/${propertyId}/pricing/requests/${requestId}`;

// SARAH-sourced request decision — RFP-scoped (not property-scoped) because it
// resumes the underlying RFP's state machine: /rfp/{pricingRequestId}/pricing/decision
export const sarahRfpDecisionPath = (pricingRequestId: string) =>
  `/rfp/${pricingRequestId}/pricing/decision`;

// SARAH-sourced request review status — RFP-scoped, same reason as above.
// Kept off the pricing-requests DTOs for module-boundary reasons; this is the
// only place pendingGpcReviewType/sarahResumeStatus/sarahResumeError live.
export const sarahRfpReviewStatusPath = (pricingRequestId: string) =>
  `/rfp/${pricingRequestId}/pricing/review-status`;

// Batch sibling — used by the list table/sidebar badge, capped at 500 ids
// server-side (MAX_PRICING_REQUEST_IDS), gated by ROLE_GPC_PRICING_TABLE_VIEW
// rather than the single-id endpoint's DETAILS_VIEW role.
export const sarahRfpReviewStatusBatchPath = () => `/rfp/pricing/review-status`;

export const gpcApi = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

gpcApi.interceptors.request.use(
  (config) => {
    const token = getTokenAccessor();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

gpcApi.interceptors.response.use(
  (response) => response,
  createResponseErrorInterceptor("group-pricing-calculator API")
);
